import { CommonUtility } from './common'

export const PRESS_SELECTION_RESET_COMPANY_ID = 34930

/** WK Large Press Audit — OriginalTemplateId + Press Selection SectionId */
export const PRESS_DOWN_WK_LARGE_ORIGINAL_TEMPLATE_ID = 60643
export const PRESS_DOWN_WK_LARGE_SECTION_ID = '0.14149930508900277'

/** WK Small Press Audit — OriginalTemplateId + Press Selection SectionId */
export const PRESS_DOWN_WK_SMALL_ORIGINAL_TEMPLATE_ID = 57357
export const PRESS_DOWN_WK_SMALL_SECTION_ID = '0.16604053328540214'

/** Large Press Daily Audit — OriginalTemplateId + Press Selection SectionId */
export const PRESS_DOWN_LARGE_PRESS_DAILY_ORIGINAL_TEMPLATE_ID = 60349
export const PRESS_DOWN_LARGE_PRESS_DAILY_SECTION_ID = '0.33551514695914353'

/** Small Press Daily Audit — OriginalTemplateId + Press Selection SectionId */
export const PRESS_DOWN_SMALL_PRESS_DAILY_ORIGINAL_TEMPLATE_ID = 157430
export const PRESS_DOWN_SMALL_PRESS_DAILY_SECTION_ID = '0.4547745232522572'

/**
 * Down button only for WK Audits + Large/Small Press Daily templates.
 * Keyed by OriginalTemplateId → allowed Press Selection SectionId(s).
 */
export const PRESS_DOWN_BY_ORIGINAL_TEMPLATE_ID = {
    [PRESS_DOWN_WK_LARGE_ORIGINAL_TEMPLATE_ID]: [
        PRESS_DOWN_WK_LARGE_SECTION_ID,
    ],
    [PRESS_DOWN_WK_SMALL_ORIGINAL_TEMPLATE_ID]: [
        PRESS_DOWN_WK_SMALL_SECTION_ID,
    ],
    [PRESS_DOWN_LARGE_PRESS_DAILY_ORIGINAL_TEMPLATE_ID]: [
        PRESS_DOWN_LARGE_PRESS_DAILY_SECTION_ID,
    ],
    [PRESS_DOWN_SMALL_PRESS_DAILY_ORIGINAL_TEMPLATE_ID]: [
        PRESS_DOWN_SMALL_PRESS_DAILY_SECTION_ID,
    ],
}

const toNumberId = value => {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? n : null
}

const normalizeSectionId = value =>
    value == null ? '' : String(value).trim()

export const getPressDownOriginalTemplateId = auditOrId => {
    if (auditOrId == null) return null
    if (typeof auditOrId === 'object') {
        return toNumberId(
            auditOrId.OriginalTemplateId ?? auditOrId.originalTemplateId,
        )
    }
    return toNumberId(auditOrId)
}

export const isPressDownTemplate = auditOrOriginalTemplateId => {
    const originalId = getPressDownOriginalTemplateId(auditOrOriginalTemplateId)
    if (originalId == null) return false
    return Object.prototype.hasOwnProperty.call(
        PRESS_DOWN_BY_ORIGINAL_TEMPLATE_ID,
        originalId,
    )
}

export const isPressDownSectionId = (sectionId, originalTemplateId) => {
    const originalId = getPressDownOriginalTemplateId(originalTemplateId)
    if (originalId == null) return false
    const allowed = PRESS_DOWN_BY_ORIGINAL_TEMPLATE_ID[originalId] || []
    const normalized = normalizeSectionId(sectionId)
    return allowed.some(id => normalizeSectionId(id) === normalized)
}

/** Down only on the Press Selection section for the allowed templates. */
export const isPressDownEligible = (section, auditOrOriginalTemplateId) =>
    isPressDownSectionId(section?.SectionId, auditOrOriginalTemplateId)

export const isPressSelectionResetEnabled = user => {
    const companyIds = [
        user?.CompanyId,
        user?.Companies?.[0]?.CompanyId,
    ]
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id) && id > 0)
    return companyIds.some(id => id === PRESS_SELECTION_RESET_COMPANY_ID)
}

const normalizeSectionName = name =>
    CommonUtility.strippedHtml(name || '')
        .trim()
        .toLowerCase()

export const isPressSelectionSectionName = name =>
    normalizeSectionName(name).includes('press selection')

export const isSelectPressQuestionFaq = faq => {
    const normalized = CommonUtility.strippedHtml(faq || '')
        .trim()
        .toLowerCase()
    if (!normalized) return false
    if (normalized === 'press') return true
    if (normalized === 'select press') return true
    // e.g. "Select Press Type" on Small Press Daily audit templates
    if (normalized.startsWith('select press')) return true
    return false
}

const getSectionSortOrder = section =>
    Number(section?.Order ?? section?.SectionOrder ?? 0)

export const getFirstInspectionSection = sections => {
    if (!Array.isArray(sections) || sections.length === 0) return null
    const sorted = [...sections].sort(
        (a, b) => getSectionSortOrder(a) - getSectionSortOrder(b),
    )
    return sorted[0] || sections[0]
}

export const findSelectPressQuestion = section => {
    const questions = section?.Questions || []
    return questions.find(q => isSelectPressQuestionFaq(q?.FAQ)) || null
}

/** When first section is Press Selection with a Select Press question. */
export const getPressResetTarget = sections => {
    const firstSection = getFirstInspectionSection(sections)
    if (!firstSection || !isPressSelectionSectionName(firstSection.SectionName)) {
        return null
    }
    const pressQuestion = findSelectPressQuestion(firstSection)
    if (!pressQuestion?.Id) return null
    return {
        sectionId: firstSection.SectionId,
        questionId: pressQuestion.Id,
    }
}

const getSelectedMultipleResponseKeys = question => {
    const responses = question?.Answers?.MultipleResponse
    if (!Array.isArray(responses)) return []
    return responses
        .filter(response => response?.IsSelected)
        .flatMap(response => {
            const keys = [
                response?.Id,
                response?.ResponseId,
                response?.ResponseUId,
                response?.ResponseUid,
                response?.UId,
                response?.Uid,
            ]
                .filter(value => value != null && String(value).trim() !== '')
                .map(value => String(value).trim())
            return keys
        })
}

const shouldPreserveMultipleResponse = (response, preserveKeys) => {
    if (!preserveKeys?.size) return false
    const candidates = [
        response?.Id,
        response?.ResponseId,
        response?.ResponseUId,
        response?.ResponseUid,
        response?.UId,
        response?.Uid,
    ]
        .filter(value => value != null && String(value).trim() !== '')
        .map(value => String(value).trim())
    return candidates.some(key => preserveKeys.has(key))
}

const clearMultipleResponse = (responses, preserveKeys) => {
    if (!Array.isArray(responses)) return responses
    return responses.map(response => {
        const keepSelected = shouldPreserveMultipleResponse(response, preserveKeys)
        return {
            ...response,
            IsSelected: keepSelected,
            Score: keepSelected ? response?.Score ?? 0 : 0,
        }
    })
}

const clearParentChildResponse = parentChildResponse => {
    if (!Array.isArray(parentChildResponse)) return parentChildResponse
    return parentChildResponse.map(parent => ({
        ...parent,
        IsSelected: false,
        Score: 0,
        IsScore: false,
        Children: Array.isArray(parent.Children)
            ? parent.Children.map(child => ({
                  ...child,
                  IsSelected: false,
                  Score: 0,
                  IsScore: false,
              }))
            : parent.Children,
    }))
}

const clearSingleResponse = singleResponse => {
    if (!singleResponse || typeof singleResponse !== 'object') {
        return singleResponse
    }
    return {
        ...singleResponse,
        Response: null,
        IsSelected: false,
    }
}

const clearQuestionAnswers = (question, preserveMultipleResponseKeys) => {
    const answers = question?.Answers || {}
    const preserveKeys = preserveMultipleResponseKeys
        ? new Set(preserveMultipleResponseKeys)
        : null
    const multipleResponse = clearMultipleResponse(
        answers.MultipleResponse,
        preserveKeys,
    )
    const hasPressSelection =
        !!preserveKeys &&
        Array.isArray(multipleResponse) &&
        multipleResponse.some(response => response?.IsSelected)

    const clearedAnswers = {
        ...answers,
        IsAnswered: hasPressSelection,
        IsOtherAnswerSelected: false,
        OtherAnswerResponse: null,
        SingleResponse: clearSingleResponse(answers.SingleResponse),
        MultipleResponse: multipleResponse,
        ParentChildResponse: clearParentChildResponse(
            answers.ParentChildResponse,
        ),
    }

    if (answers.CNCResponse) {
        clearedAnswers.CNCResponse = null
    }

    const clearedQuestion = {
        ...question,
        Score: hasPressSelection ? question?.Score ?? 0 : 0,
        IsAnswered: hasPressSelection,
        Files: [],
        Notes: [],
        Comments: [],
        Answers: clearedAnswers,
    }

    if (Object.prototype.hasOwnProperty.call(question, 'NCReply')) {
        clearedQuestion.NCReply = null
    }
    if (Object.prototype.hasOwnProperty.call(question, 'CNCResponse')) {
        clearedQuestion.CNCResponse = null
    }

    return clearedQuestion
}

/** Clears every answer in the inspection except the selected press value. */
export const clearInspectionKeepingPressSelection = (sections, pressQuestionId) => {
    if (!Array.isArray(sections) || pressQuestionId == null) {
        return sections
    }

    const pressKey = String(pressQuestionId)
    let preservedMultipleResponseKeys = []

    sections.forEach(section => {
        const questions = section?.Questions || []
        questions.forEach(question => {
            if (String(question?.Id) === pressKey) {
                preservedMultipleResponseKeys =
                    getSelectedMultipleResponseKeys(question)
            }
        })
    })

    return sections.map(section => ({
        ...section,
        Questions: (section?.Questions || []).map(question => {
            const isPressQuestion = String(question?.Id) === pressKey
            return clearQuestionAnswers(
                question,
                isPressQuestion ? preservedMultipleResponseKeys : null,
            )
        }),
    }))
}
